import { AlertCircle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { Button } from "@/components/ui/button"

/**
 * OKS-38 — Error state for the brand dashboard.
 *
 * Replaces the entire dashboard surface (KPI + deadlines) when the
 * controller's data composition raised (TRD §K19 — `error: true` prop).
 *
 * The Retry button calls back into the page, which issues an Inertia
 * partial reload. While the round-trip is in flight, the parent passes
 * `isRetrying=true` so the button disables + the label switches to the
 * "Retrying…" copy (UX Spec §4 — "disabled while reload in flight").
 */
export interface DashboardErrorStateProps {
  onRetry: () => void
  isRetrying: boolean
}

export function DashboardErrorState({
  onRetry,
  isRetrying,
}: DashboardErrorStateProps) {
  const { t } = useTranslation()

  return (
    <div data-testid="dashboard-error-state">
      <EmptyState
        icon={AlertCircle}
        title={t("brand_home.error.title")}
        description={t("brand_home.error.description")}
        actions={
          <Button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            data-testid="dashboard-error-retry"
          >
            {isRetrying
              ? t("brand_home.error.retry_in_progress")
              : t("brand_home.error.retry")}
          </Button>
        }
      />
    </div>
  )
}
